import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";

const workplaces = [
  {
    id: "recruitment",
    title: "RECRUITMENT",
    subtitle: "Manage requisitions, applicants, and hiring decisions.",
    icon: "fa-users",
    route: "/recruitment-hexagon",
    tone: "blue",
    tags: ["Requisitions", "Applicants", "Offers"],
    features: [
      "Create Requisitions",
      "Post Requisitions",
      "Applicant Response",
      "Review -Extend Offer /Reject",
    ],
  },
  {
    id: "onboarding",
    title: "ONBOARDING",
    subtitle: "Run candidate, recruiter, and subcontractor onboarding workflows.",
    icon: "fa-briefcase",
    route: "/onboarding-hexagon",
    tone: "gold",
    tags: ["Candidates", "Contracts", "Tasks"],
    features: [],
  },
  {
    id: "safety",
    title: "SAFETY MANAGEMENT",
    subtitle: "Track safety, compliance, hazards, and accountability programs.",
    icon: "fa-shield",
    route: "/safety-hexagon",
    tone: "green",
    tags: ["Incidents", "Compliance", "Reports"],
    features: [
      "Incident Management",
      "Job Hazard Management",
      "Enterprise Risk Register",
      "Safety Inspection Management",
      "Training Management",
      "Compliance Management",
      "Environmental Health & Hazard Observations",
      "Reports and Accountability Tracking",
    ],
  },
];

const HexagonLanding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setCurrentPageTitle("Choose Workplace"));
    dispatch(setCurrentRoute([{ name: "Choose Workplace", url: "" }]));
  }, [dispatch]);

  const handleWorkplaceSelect = (workplace) => {
    localStorage.setItem("selectedWorkplace", workplace.id);
    navigate(workplace.route, { state: { workplace: workplace.id } });
  };

  return (
    <div className="wf-workplace-page">
      <style>{`
        .wf-workplace-page {
          min-height: 100vh;
          padding: 28px 24px 40px;
          color: #0f172a;
          background:
            radial-gradient(circle at 10% 8%, rgba(37, 99, 235, 0.08), transparent 28%),
            radial-gradient(circle at 90% 12%, rgba(16, 185, 129, 0.08), transparent 26%),
            linear-gradient(180deg, rgba(248, 250, 252, 0.94), rgba(255, 255, 255, 0.98)),
            #f8fafc;
          font-family: "Inter", "Segoe UI", Roboto, sans-serif;
          overflow-y: auto;
        }

        .wf-workplace-shell {
          width: min(1180px, 100%);
          margin: 0 auto;
        }

        .wf-workplace-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 24px;
          margin-bottom: 30px;
          padding: 22px 24px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
        }

        .wf-workplace-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          color: #4766e8;
          font-size: 11px;
          font-weight: 850;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .wf-workplace-title {
          margin: 0;
          color: #0f172a;
          font-size: 27px;
          font-weight: 900;
          line-height: 1.14;
          letter-spacing: 0;
        }

        .wf-workplace-copy {
          max-width: 660px;
          margin: 10px 0 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.55;
        }

        .wf-workplace-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          color: #475569;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 800;
          white-space: nowrap;
        }

        .wf-workplace-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 22px;
          align-items: stretch;
        }

        .wf-workplace-card {
          --workplace-accent: #2563eb;
          --workplace-soft: #eff6ff;
          position: relative;
          min-height: 560px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 28px 22px 24px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          background: #ffffff;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
          cursor: pointer;
          overflow: hidden;
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }

        .wf-workplace-card::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 5px;
          background: var(--workplace-accent);
        }

        .wf-workplace-card:hover,
        .wf-workplace-card:focus-visible {
          transform: translateY(-4px);
          border-color: var(--workplace-accent);
          box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
          outline: none;
        }

        .wf-workplace-card.tone-blue {
          --workplace-accent: #2563eb;
          --workplace-soft: #eff6ff;
        }

        .wf-workplace-card.tone-gold {
          --workplace-accent: #eab308;
          --workplace-soft: #fef9c3;
        }

        .wf-workplace-card.tone-green {
          --workplace-accent: #10b981;
          --workplace-soft: #ecfdf5;
        }

        .wf-workplace-hex {
          width: 164px;
          height: 184px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--workplace-soft);
          clip-path: polygon(50% 0%, 94% 25%, 94% 75%, 50% 100%, 6% 75%, 6% 25%);
          position: relative;
          flex-shrink: 0;
        }

        .wf-workplace-hex::after {
          content: "";
          position: absolute;
          inset: 12px;
          background: #ffffff;
          clip-path: inherit;
          box-shadow: inset 0 0 0 2px rgba(148, 163, 184, 0.18);
        }

        .wf-workplace-hex i {
          position: relative;
          z-index: 1;
          color: var(--workplace-accent);
          font-size: 42px;
        }

        .wf-workplace-card-title {
          margin: 0;
          color: #0f172a;
          font-size: 18px;
          font-weight: 900;
          line-height: 1.2;
          letter-spacing: 0.04em;
          text-align: center;
        }

        .wf-workplace-card-copy {
          min-height: 44px;
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.5;
          text-align: center;
        }

        .wf-workplace-tags {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 7px;
        }

        .wf-workplace-tag {
          display: inline-flex;
          align-items: center;
          padding: 5px 8px;
          color: #475569;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .wf-workplace-features {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin: 2px 0 0;
          padding: 0;
          list-style: none;
        }

        .wf-workplace-feature {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          color: #334155;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.35;
          text-align: left;
        }

        .wf-workplace-feature i {
          margin-top: 2px;
          color: var(--workplace-accent);
          font-size: 10px;
          flex-shrink: 0;
        }

        .wf-workplace-empty-features {
          width: 100%;
          min-height: 128px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          font-size: 12px;
          font-weight: 750;
          line-height: 1.45;
          text-align: center;
          border: 1px dashed #e2e8f0;
          border-radius: 8px;
          background: #f8fafc;
        }

        .wf-workplace-open {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: auto;
          padding: 9px 13px;
          color: var(--workplace-accent);
          background: var(--workplace-soft);
          border: 1px solid rgba(148, 163, 184, 0.35);
          border-radius: 8px;
          font-size: 12px;
          font-weight: 850;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .wf-workplace-card:hover .wf-workplace-open {
          border-color: var(--workplace-accent);
        }

        @media (max-width: 980px) {
          .wf-workplace-grid {
            grid-template-columns: 1fr;
          }

          .wf-workplace-card {
            min-height: auto;
          }

          .wf-workplace-header {
            align-items: flex-start;
            flex-direction: column;
          }
        }

        @media (max-width: 560px) {
          .wf-workplace-page {
            padding: 18px 12px 28px;
          }

          .wf-workplace-title {
            font-size: 22px;
          }

          .wf-workplace-hex {
            width: 140px;
            height: 158px;
          }
        }
      `}</style>

      <div className="wf-workplace-shell">
        <header className="wf-workplace-header">
          <div>
            <span className="wf-workplace-kicker">
              <i className="fa fa-th-large" aria-hidden="true" />
              Workplace Portal
            </span>
            <h1 className="wf-workplace-title">
              Human Resources Information Management System (HRIMS)
            </h1>
            <p className="wf-workplace-copy">
              Choose the workplace area you want to open. Each option keeps its own focused workflow space and feature set.
            </p>
          </div>
          <span className="wf-workplace-badge">
            <i className="fa fa-check-circle" aria-hidden="true" />
            Authenticated session
          </span>
        </header>

        <section className="wf-workplace-grid" aria-label="Workplace options">
          {workplaces.map((workplace) => (
            <button
              key={workplace.id}
              type="button"
              className={`wf-workplace-card tone-${workplace.tone}`}
              onClick={() => handleWorkplaceSelect(workplace)}
            >
              <span className="wf-workplace-hex" aria-hidden="true">
                <i className={`fa ${workplace.icon}`} />
              </span>
              <h2 className="wf-workplace-card-title">{workplace.title}</h2>
              <p className="wf-workplace-card-copy">{workplace.subtitle}</p>
              <span className="wf-workplace-tags">
                {workplace.tags.map((tag) => (
                  <span key={tag} className="wf-workplace-tag">{tag}</span>
                ))}
              </span>
              {workplace.features.length > 0 ? (
                <ul className="wf-workplace-features">
                  {workplace.features.map((feature) => (
                    <li key={feature} className="wf-workplace-feature">
                      <i className="fa fa-check" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="wf-workplace-empty-features">
                  Open onboarding workflows, contracts, documents, and task reviews.
                </span>
              )}
              <span className="wf-workplace-open">
                Open Workplace
                <i className="fa fa-arrow-right" aria-hidden="true" />
              </span>
            </button>
          ))}
        </section>
      </div>
    </div>
  );
};

export default HexagonLanding;
